import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { FileSignature, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { ParentFormFill } from "@/components/parent/ParentFormFill";
import { SECOND_SIGNER_LABEL } from "@/components/forms/fieldTypes";

/** Fill & sign a form straight from the email link — no parent sign-in required. */
export default function PublicFormPage() {
  const { token } = useParams();
  const client = useMemo(() => axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [send, setSend] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const load = async () => {
    try {
      const { data } = await client.get("/parent/forms");
      if (!data.length) { setError("This form is no longer available."); return; }
      setSend(data[0]);
    } catch (e) {
      setError(e.response?.status === 401 ? "This link has expired or is invalid. Ask the school to resend the form, or sign in to the parent app." : "Could not load the form.");
    }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const actionable = send && (send.stage === "first" || send.stage === "second");
  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-form-page">
      <header className="bg-white border-b border-slate-200 px-5 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-indigo-500/15 flex items-center justify-center"><FileSignature className="h-4 w-4 text-indigo-600" /></div>
          <div>
            <div className="font-display font-bold leading-tight">{send?.school_name || "Logerd"} · Forms</div>
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Secure personal link · no sign-in needed</div>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {!send && !error && <div className="rounded-2xl bg-white p-10 text-center shadow-sm"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>}
        {error && <div className="rounded-2xl bg-white p-8 text-center shadow-sm text-sm text-slate-600" data-testid="public-form-error">{error}<div className="mt-4"><Link to="/parent/login" className="text-indigo-600 font-semibold hover:underline">Open the parent app</Link></div></div>}
        {send && done && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm" data-testid="public-form-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
            <div className="font-display text-xl font-bold mt-3">Thank you!</div>
            <p className="text-sm text-slate-600 mt-1">{send.form?.second_signer && send.stage === "first" ? `Your signature is recorded. The form is complete once the ${SECOND_SIGNER_LABEL[send.form.second_signer].toLowerCase()} signs too.` : `“${send.form_title}” for ${send.student_name} has been signed and sent to ${send.school_name}.`}</p>
            <Link to="/parent/login" className="inline-block mt-5 text-sm text-indigo-600 font-semibold hover:underline">Open the parent app</Link>
          </div>
        )}
        {send && !done && actionable && <ParentFormFill send={send} client={client} hideBack onClose={() => {}} onDone={() => setDone(true)} />}
        {send && !done && !actionable && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm text-sm text-slate-600" data-testid="public-form-status">
            {send.status === "completed" ? `“${send.form_title}” is already fully signed. Nothing else to do.` : `You already signed this form — waiting for ${send.second_signer === "parent" ? "the other parent/guardian" : "the school"} to countersign.`}
          </div>
        )}
      </main>
    </div>
  );
}
